/**
 * Dashboard data-binding contract.
 *
 * This EXTENDS the existing imported-dashboard model (see
 * `components/DashboardImporter.ts` → `DashboardComponent`) rather than
 * replacing it. A component keeps its `type` / `position` / `size` / `style`;
 * the new, optional `dataBinding` in its `props` tells the runtime WHERE the
 * data comes from (NATS live, Doris history, or Hybrid) and WHICH channels to
 * plot. Components without a `dataBinding` render exactly as before.
 *
 * This is the serialised contract shared with the external dashboard editor —
 * keep it backward compatible and bump `DATA_BINDING_VERSION` on breaking
 * changes so imports can be migrated instead of failing.
 */

export const DATA_BINDING_VERSION = 1;

/** Per-panel data source. */
export type SourceSpec =
  /** Live only, from the NATS JetStream live stream. */
  | { mode: 'nats' }
  /** History only, from Doris (OLAP). */
  | { mode: 'doris'; resolution?: 'auto' | 'raw' | 'hourly' }
  /**
   * History from Doris up to `boundary`, then the live NATS tail after it.
   * `boundary: 'live'` means "everything before now from Doris, now onward from
   * NATS"; an ISO string pins an explicit cutover.
   */
  | { mode: 'hybrid'; boundary?: 'live' | string };

/**
 * How a single plotted channel locates its data in each source. Both matchers
 * are optional: when omitted, the runtime falls back to the built-in
 * schema-agnostic resolvers (energy sensor map / motor subject parser), so the
 * app's own dashboards need not spell everything out.
 */
export interface ChannelSpec {
  /** Canonical id used by the visualization + legend. */
  key: string;
  label?: string;
  unit?: string;
  color?: string;
  /** Match against a NATS subject's final segment(s). */
  nats?: {
    /** Normalised aliases (lower-case, alphanumeric) for the last segment. */
    aliases?: string[];
    /** Optional full-subject regex (advanced). */
    regex?: string;
  };
  /** Match against a Doris `sensor_name`. */
  doris?: {
    /** `sensor_prefix` LIKE prefix (without the trailing %). */
    prefix?: string;
    /** Bare signal names that map to this channel (e.g. "TorqueRmsValue"). */
    sensorAliases?: string[];
    /** Axis token to scope a component-wide query (e.g. "MA03"). */
    axisToken?: string;
  };
  /** Optional value transform applied per point. */
  transform?: 'raw' | 'rate' | 'sum';
}

/** The binding attached to a dashboard component's `props.dataBinding`. */
export interface DataBinding {
  version: number;
  /** Machine business key / serial, or a `${variable}` resolved at render time. */
  machineRef: string;
  source: SourceSpec;
  channels: ChannelSpec[];
}

/** A resolved time range the providers understand. */
export type ResolvedRange =
  | { kind: 'live' }
  | { kind: 'window'; startMs: number; endMs: number };

/** Narrow a value to a DataBinding if it looks like one. */
export function asDataBinding(value: unknown): DataBinding | null {
  if (!value || typeof value !== 'object') return null;
  const b = value as Partial<DataBinding>;
  if (!b.source || !Array.isArray(b.channels) || typeof b.machineRef !== 'string') return null;
  return b as DataBinding;
}
